import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, catchError, delay, map, of, switchMap, tap } from 'rxjs';

export interface ValidateReferralResponse {
  isValid: boolean;
  isInUse: boolean;
  sessionId: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileCaptureService {

  readonly SESSION_KEY = 'fc-ref-session-id';
  readonly SESSION_BACKUP_KEY = 'fc-ref-session-id-backup';
  readonly LOCKED_REFERENCE_KEY = 'fc-locked-reference-id';
  readonly PAYOR_KEY = 'fc-payor-key';
  readonly EMAIL_AUTH_APPLICATION_CODE = 'FileCapture';

  baseUrl = environment.baseUri;

  private fileUploadStartSubject = new BehaviorSubject<File[] | null>(null);
  fileArray: File[] = [];

  constructor(private http: HttpClient) { }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    if (typeof value === 'number') return value === 1;
    return false;
  }

  private normalizeAttachmentResponse(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    return {
      ...data,
      attachmentKey: data.attachmentKey ?? data.AttachmentKey,
      caseActivityKey: data.caseActivityKey ?? data.CaseActivityKey
    };
  }

  public getReferenceId(referenceId: string | null, payorkey: number, sessionId: string = ''): Observable<ValidateReferralResponse> {
    let params = new HttpParams()
      .set('referenceid', referenceId ?? '')
      .set('payorkey', String(payorkey ?? ''));

    if (sessionId) {
      params = params.set('sessionId', sessionId);
    }

    return this.http.get<any>(this.baseUrl + 'FileCapture/ValidateReferal', { params }).pipe(
      map((r: any) => ({
        isValid: this.toBoolean(r?.isValid ?? r?.IsValid),
        isInUse: this.toBoolean(r?.isInUse ?? r?.IsInUse),
        sessionId: r?.sessionId ?? r?.SessionId ?? sessionId
      }))
    );
  }

  public releaseReferenceLock(referenceId: string | null, payorKey: number, sessionId: string): Observable<any> {
    const params = new HttpParams()
      .set('referenceid', referenceId ?? '')
      .set('payorkey', String(payorKey ?? ''))
      .set('sessionId', sessionId);
    return this.http.get<any>(this.baseUrl + 'FileCapture/ReleaseLock', { params });
  }

  public setActiveReferenceSession(referenceId: string | null, sessionId: string | null): void {
    if (!sessionId) {
      this.clearActiveReferenceSession();
      return;
    }

    window.sessionStorage.setItem(this.SESSION_KEY, sessionId);
    window.localStorage.setItem(this.SESSION_BACKUP_KEY, sessionId);

    if (referenceId) {
      window.localStorage.setItem(this.LOCKED_REFERENCE_KEY, referenceId);
    }
  }

  public getActiveReferenceSessionId(fallbackSessionId: string = ''): string {
    return fallbackSessionId ||
      window.sessionStorage.getItem(this.SESSION_KEY) ||
      window.localStorage.getItem(this.SESSION_BACKUP_KEY) ||
      '';
  }

  public getActiveReferenceId(fallbackReferenceId: string | null = ''): string {
    return fallbackReferenceId ||
      window.localStorage.getItem(this.LOCKED_REFERENCE_KEY) ||
      '';
  }

  public clearActiveReferenceSession(clearStoredLock: boolean = true): void {
    window.sessionStorage.removeItem(this.SESSION_KEY);

    if (clearStoredLock) {
      window.localStorage.removeItem(this.SESSION_BACKUP_KEY);
      window.localStorage.removeItem(this.LOCKED_REFERENCE_KEY);
    }
  }

  public releaseActiveReferenceLock(payorKey: number, referenceId?: string | null, sessionId?: string | null): Observable<any> {
    const activeReferenceId = this.getActiveReferenceId(referenceId || '');
    const activeSessionId = this.getActiveReferenceSessionId(sessionId || '');

    if (!activeReferenceId || !activeSessionId || payorKey <= 0) {
      this.clearActiveReferenceSession();
      return of(null);
    }

    return this.releaseReferenceLock(activeReferenceId, payorKey, activeSessionId).pipe(
      tap(() => this.clearActiveReferenceSession()),
      catchError(() => {
        this.clearActiveReferenceSession(false);
        return of(null);
      })
    );
  }

  public submitDocument(data: any | null): Observable<boolean> {
    const sessionId = this.getActiveReferenceSessionId();
    const params = new HttpParams().set('sessionId', sessionId);
    console.log('[FileCapture][Submit] request', {
      sessionId,
      payload: {
        referenceId: data?.ReferenceId ?? data?.referenceId,
        caseActivityKey: data?.CaseActivityKey ?? data?.caseActivityKey,
        activeAttachmentKey: data?.ActiveAttachmentKey ?? data?.activeAttachmentKey,
        activeAttachmentKeyJson: JSON.stringify(data?.ActiveAttachmentKey ?? data?.activeAttachmentKey ?? [])
      }
    });

    return this.http.post<boolean>(this.baseUrl + "FileCapture/Submit?Submit", data, { params }).pipe(
      tap({
        next: (response: boolean) => {
          console.log('[FileCapture][Submit] response', {
            sessionId,
            referenceId: data?.ReferenceId ?? data?.referenceId,
            response
          });
        },
        error: (error: any) => {
          console.log('[FileCapture][Submit] error', {
            sessionId,
            referenceId: data?.ReferenceId ?? data?.referenceId,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });
        }
      })
    );
  }

  getUsersTimeZone() {
        var timezoneMapping: { [key: string]: string } = {
            'Asia/Calcutta': 'India Standard Time',
            'America/New_York': 'US Eastern Standard Time',
            'America/Chicago': 'Central Standard Time',
            'America/Los_Angeles': 'Pacific Standard Time',
            'America/Denver': 'Mountain Standard Time',
            'America/Anchorage': 'Alaskan Standard Time',
            'Pacific/Honolulu': 'Hawaiian Standard Time',
            'America/Tijuana': 'Pacific Standard Time',
            'America/Phoenix': 'Mountain Standard Time'
        }
        var zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return timezoneMapping[zone] || 'Central Standard Time';
    }

  public addAttachmentToDB(data: any | null): Observable<any> {
    const sessionId = this.getActiveReferenceSessionId();
    const params = new HttpParams().set('sessionId', sessionId);
    var timeZoneName = this.getUsersTimeZone();
    data.timeZoneName = timeZoneName;

    console.log('[FileCapture][AddAttachmentToDB] request', {
      referenceId: data?.referenceId,
      fileName: data?.fileName,
      isFirst: data?.isFirst,
      caseActivityKey: data?.caseActivityKey,
      attachmentLength: data?.attachmentLength,
      sessionId
    });

    return this.http.post<any>(this.baseUrl + "FileCapture/AddAttachmentToDB", data, { params }).pipe(
      map((response: any) => this.normalizeAttachmentResponse(response)),
      tap({
        next: (response: any) => {
          console.log('[FileCapture][AddAttachmentToDB] response', {
            referenceId: data?.referenceId,
            fileName: data?.fileName,
            isFirst: data?.isFirst,
            caseActivityKey: data?.caseActivityKey,
            sessionId,
            response
          });
        },
        error: (error: any) => {
          console.log('[FileCapture][AddAttachmentToDB] error', {
            referenceId: data?.referenceId,
            fileName: data?.fileName,
            isFirst: data?.isFirst,
            caseActivityKey: data?.caseActivityKey,
            sessionId,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });
        }
      })
    );
  }

  public commitFile(attachmentKey: any | null, fileName: any, totalChunks: any): Observable<number> {
    return this.http.get<number>(this.baseUrl + "FileCapture/CommitFile?attachmentKey=" + attachmentKey + "&fileName=" + fileName + "&totalChunks=" + totalChunks);
  }

  public ReviewAndScanAttachmnents(data: any | null): Observable<boolean> {
    console.log('[FileCapture][ReviewAndScanAttachmnents] request', {
      referenceId: data?.referenceId,
      attachmentKeyList: data?.attachmentKeyList
    });

    return this.http.post<boolean>(this.baseUrl + "FileCapture/ReviewAndScanAttachmnents", data).pipe(
      tap({
        next: (response: boolean) => {
          console.log('[FileCapture][ReviewAndScanAttachmnents] response', {
            referenceId: data?.referenceId,
            attachmentKeyList: data?.attachmentKeyList,
            response
          });
        },
        error: (error: any) => {
          console.log('[FileCapture][ReviewAndScanAttachmnents] error', {
            referenceId: data?.referenceId,
            attachmentKeyList: data?.attachmentKeyList,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });
        }
      })
    );
  }

  public GetAttachmentStatus(data: any | null): Observable<any> {
    console.log('[FileCapture][GetAttachmentStatus] request', {
      referenceId: data?.referenceId,
      attachmentKeyList: data?.attachmentKeyList
    });

    return this.http.post<any>(this.baseUrl + "FileCapture/GetAttachmentStatus", data).pipe(
      tap({
        next: (response: any) => {
          console.log('[FileCapture][GetAttachmentStatus] response', {
            referenceId: data?.referenceId,
            attachmentKeyList: data?.attachmentKeyList,
            response: Array.isArray(response)
              ? response.map((item: any) => ({
                  id: item?.id ?? item?.Id,
                  virusScanStatus: item?.VirusScanStatus ?? item?.virusScanStatus,
                  fileName: item?.fileName ?? item?.FileName
                }))
              : response
          });
        },
        error: (error: any) => {
          console.log('[FileCapture][GetAttachmentStatus] error', {
            referenceId: data?.referenceId,
            attachmentKeyList: data?.attachmentKeyList,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });
        }
      })
    );
  }

  public pollStatus(interval: number, data: any | null): Observable<any> {
    return this.GetAttachmentStatus(data).pipe(switchMap(status => {
      if (status.filter(x => x.VirusScanStatus == null || x.VirusScanStatus == 0).length == 0) {
        return of(status);
      }
      else {
        return of(status).pipe(delay(interval), switchMap(() => this.pollStatus(interval, data)));
      }
    }));
  }

  public uploadAttachment(data: any | null, sourceAppKey: any, sourceKey: any, activityKey: any, attachmentKey: any, referenceId: any): Observable<number> {
    return this.http.post<number>(this.baseUrl + "FileCapture/UploadAttachment?sourceAppKey=" + sourceAppKey + "&sourceKey=" + sourceKey + "&activityKey=" + activityKey + "&attachmentKey=" + attachmentKey + "&referenceId" + referenceId, data);
  }

  public generateCode(contactEmail: string | null, refid: string | null, applicationCode: string | null): Observable<any> {
    const params = new HttpParams()
      .set('contactEmail', contactEmail ?? '')
      .set('refid', refid ?? '')
      .set('applicationCode', applicationCode || this.EMAIL_AUTH_APPLICATION_CODE);

    return this.http.post<any>(this.baseUrl + 'EmailAuth/GenerateCode', null, { params });
  }

  public validateCode(data: any | null): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "EmailAuth/ValidateCode", data);
  }

  public addFilesToDB(data: any | null): Observable<HttpEvent<number>> {
    var timeZoneName = this.getUsersTimeZone();
    data.timeZoneName = timeZoneName;
    return this.http.post<number>(this.baseUrl + "FileCapture/addFilesToDB", data, {
      reportProgress: true,
      observe: 'events'
    });
  }

  sendUploadStartData(data: File[]) {
    this.fileUploadStartSubject.next(data);
  }

  getUploadStartData(): File[] | null {
    return this.fileUploadStartSubject.getValue();
  }

  deleteAttachment(data: any | null): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "FileCapture/DeleteAttachment", data);
  }

  public getPayorIdByCode(securityCode: string | null): Observable<number> {
    return this.http.get<number>(this.baseUrl + "FileCapture/GetPayorIdByCode?securityCode=" + securityCode);
  }

  // <LLM_CODE_BEGIN story="US_826609">
  public getFileCaptureConfig(securityCode: string | null): Observable<any> {
    return this.http.get<any>(this.baseUrl + "FileCapture/GetFileCaptureConfig?securityCode=" + securityCode);
  }
  // <LLM_CODE_END story="US_826609">
}
